import { z } from "zod";

/*
 * ActorMemory
 *
 * Persistent memory layer for actor instances — an actor's identity across
 * wake cycles. Complements conversation history (which is per-run) and
 * identityPrompt (which is static).
 *
 * Two layers in this first cut (procedural deferred):
 *   - episodic:  "what happened" — specific interactions or events
 *   - semantic:  "what I know"   — consolidated facts, preferences, beliefs
 */

export const actorMemoryLayerSchema = z.enum(["episodic", "semantic"]);

/**
 * Free-form categorization within a layer. Callers pick their own kinds
 * (e.g. "interaction", "feedback", "fact", "preference") — the repository
 * doesn't enforce a closed set. Keep it short and lowercase by convention.
 */
export const actorMemoryKindSchema = z.string().trim().min(1).max(64);

export const actorMemoryEntrySchema = z.object({
  accessCount: z.number().int().nonnegative(),
  actorInstanceId: z.string(),
  body: z.string(),
  createdAt: z.string().datetime(),
  expiresAt: z.string().datetime().nullable(),
  id: z.string(),
  kind: actorMemoryKindSchema,
  lastAccessedAt: z.string().datetime().nullable(),
  layer: actorMemoryLayerSchema,
  metadata: z.record(z.string(), z.unknown()).nullable(),
  organizationId: z.string(),
  sourceRunId: z.string().nullable(),
  summary: z.string(),
  supersededById: z.string().nullable(),
  updatedAt: z.string().datetime(),
});

export const createActorMemoryEntrySchema = z.object({
  actorInstanceId: z.string().trim().min(1),
  body: z.string().trim().min(1),
  expiresAt: z.string().datetime().optional(),
  kind: actorMemoryKindSchema,
  layer: actorMemoryLayerSchema,
  metadata: z.record(z.string(), z.unknown()).optional(),
  organizationId: z.string().trim().min(1),
  sourceRunId: z.string().trim().min(1).optional(),
  summary: z.string().trim().min(1).max(512),
});

/**
 * Partial update. Does not allow changing actorInstanceId, organizationId,
 * layer, or kind — those are identity-shaped; create a new entry instead.
 */
export const updateActorMemoryEntrySchema = z.object({
  body: z.string().trim().min(1).optional(),
  expiresAt: z.string().datetime().nullable().optional(),
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  summary: z.string().trim().min(1).max(512).optional(),
  supersededById: z.string().trim().min(1).nullable().optional(),
});

/**
 * Filter shape for queries. All fields optional; repository applies them
 * conjunctively. Returns entries ordered by createdAt DESC by default.
 */
export const actorMemoryQuerySchema = z.object({
  actorInstanceId: z.string().trim().min(1),
  includeExpired: z.boolean().optional(),
  includeSuperseded: z.boolean().optional(),
  kind: actorMemoryKindSchema.optional(),
  layer: actorMemoryLayerSchema.optional(),
  limit: z.number().int().positive().max(1000).optional(),
  organizationId: z.string().trim().min(1),
});
